import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Cpu, Layers, Minus, Plus, ShoppingCart, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import TrainingPackageCheckout from '@/components/payments/TrainingPackageCheckout';
import {
  getSelfServeAddonById,
  getSelfServeTierById,
  selfServeAddons,
  selfServeTiers,
  type SelfServeAddon,
  type SelfServeAddonId,
  type SelfServeTier,
  type SelfServeTierId,
} from '@/lib/marketing';

const TIER_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'fine-tuning': Cpu,
  'pre-training': Layers,
  frontier: Sparkles,
};

const ADDON_GROUP_LABEL: Record<SelfServeAddon['category'], string> = {
  training: 'Training upgrades',
  deployment: 'Deployment & hosting',
  evaluation: 'Evaluation & analysis',
};

type PackageCart = Partial<Record<SelfServeTierId, number>>;
type AddonCart = Partial<Record<SelfServeAddonId, number>>;

function parsePackageCart(raw: string | null): PackageCart {
  if (!raw) return {};

  return raw.split(',').reduce((acc, entry) => {
    const [id, quantityRaw] = entry.split(':');
    const tier = getSelfServeTierById(id);
    const quantity = Math.max(1, Math.min(99, Number(quantityRaw) || 1));
    if (tier) {
      acc[tier.id] = quantity;
    }
    return acc;
  }, {} as PackageCart);
}

function parseAddonCart(raw: string | null): AddonCart {
  if (!raw) return {};

  return raw.split(',').reduce((acc, entry) => {
    const [id, quantityRaw] = entry.split(':');
    const addon = getSelfServeAddonById(id);
    const quantity = Math.max(1, Math.min(99, Number(quantityRaw) || 1));
    if (addon) {
      acc[addon.id] = quantity;
    }
    return acc;
  }, {} as AddonCart);
}

function serializePackageCart(cart: PackageCart) {
  return Object.entries(cart)
    .filter(([, quantity]) => (quantity || 0) > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, quantity]) => `${id}:${quantity}`)
    .join(',');
}

function serializeAddonCart(cart: AddonCart) {
  return Object.entries(cart)
    .filter(([, quantity]) => (quantity || 0) > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, quantity]) => `${id}:${quantity}`)
    .join(',');
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export default function SelfServePricing() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTier, setSelectedTier] = useState<SelfServeTier | null>(null);
  const [packageCart, setPackageCart] = useState<PackageCart>({});
  const [addonCart, setAddonCart] = useState<AddonCart>({});
  const [cartCheckoutOpen, setCartCheckoutOpen] = useState(false);
  const [cartCheckoutLoading, setCartCheckoutLoading] = useState(false);

  const grouped = selfServeAddons.reduce(
    (acc, addon) => {
      (acc[addon.category] ||= []).push(addon);
      return acc;
    },
    {} as Record<SelfServeAddon['category'], SelfServeAddon[]>,
  );
  const categoryOrder: SelfServeAddon['category'][] = ['training', 'evaluation', 'deployment'];

  const selectedTierIcon = useMemo(() => {
    if (!selectedTier) return Cpu;
    return TIER_ICONS[selectedTier.id] ?? Cpu;
  }, [selectedTier]);
  const SelectedTierIcon = selectedTierIcon;

  const packageCartItems = useMemo(
    () =>
      Object.entries(packageCart)
        .map(([id, quantity]) => {
          const tier = getSelfServeTierById(id);
          if (!tier || !quantity) return null;
          return { tier, quantity };
        })
        .filter((item): item is { tier: SelfServeTier; quantity: number } => !!item),
    [packageCart],
  );

  const addonCartItems = useMemo(
    () =>
      Object.entries(addonCart)
        .map(([id, quantity]) => {
          const addon = getSelfServeAddonById(id);
          if (!addon || !quantity) return null;
          return { addon, quantity };
        })
        .filter((item): item is { addon: SelfServeAddon; quantity: number } => !!item),
    [addonCart],
  );

  const packageCreditCount = useMemo(
    () => packageCartItems.reduce((sum, item) => sum + item.quantity, 0),
    [packageCartItems],
  );

  const addonItemCount = useMemo(
    () => addonCartItems.reduce((sum, item) => sum + item.quantity, 0),
    [addonCartItems],
  );

  const cartItemCount = packageCreditCount + addonItemCount;
  const hasCartItems = cartItemCount > 0;

  const oneTimeTotal = useMemo(
    () =>
      packageCartItems.reduce((sum, item) => sum + item.tier.amountCents * item.quantity, 0)
      + addonCartItems
        .filter((item) => item.addon.billingType === 'one_time')
        .reduce((sum, item) => sum + item.addon.amountCents * item.quantity, 0),
    [addonCartItems, packageCartItems],
  );

  const recurringMonthlyTotal = useMemo(
    () =>
      addonCartItems
        .filter((item) => item.addon.billingType === 'recurring')
        .reduce((sum, item) => sum + item.addon.amountCents * item.quantity, 0),
    [addonCartItems],
  );

  const cartSummaryLabel = useMemo(() => {
    if (!hasCartItems) return 'Your self-serve cart is empty';

    const segments = [] as string[];
    if (packageCreditCount > 0) {
      segments.push(`${packageCreditCount} package credit${packageCreditCount === 1 ? '' : 's'}`);
    }
    if (addonItemCount > 0) {
      segments.push(`${addonItemCount} add-on${addonItemCount === 1 ? '' : 's'}`);
    }
    return `${segments.join(' and ')} in cart`;
  }, [addonItemCount, hasCartItems, packageCreditCount]);

  const syncCartInUrl = (nextPackageCart: PackageCart, nextAddonCart: AddonCart, openCheckout: boolean) => {
    const params = new URLSearchParams(location.search);
    params.delete('cart_success');
    params.delete('cart_canceled');
    params.delete('session_id');

    const serializedPackages = serializePackageCart(nextPackageCart);
    const serializedAddons = serializeAddonCart(nextAddonCart);

    if (serializedPackages) {
      params.set('cart_packages', serializedPackages);
    } else {
      params.delete('cart_packages');
    }

    if (serializedAddons) {
      params.set('cart_addons', serializedAddons);
    } else {
      params.delete('cart_addons');
    }

    if (openCheckout && (serializedPackages || serializedAddons)) {
      params.set('cart_checkout', '1');
    } else {
      params.delete('cart_checkout');
    }

    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}${location.hash || ''}`, {
      replace: true,
    });
  };

  const closePackageCheckoutModal = () => {
    setSelectedTier(null);

    const params = new URLSearchParams(location.search);
    if (params.has('package') || params.has('checkout')) {
      params.delete('package');
      params.delete('checkout');
      navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}${location.hash || ''}`, {
        replace: true,
      });
    }
  };

  const setPackageQuantity = (tierId: SelfServeTierId, quantity: number) => {
    setPackageCart((prev) => {
      const next = { ...prev };
      if (quantity <= 0) {
        delete next[tierId];
      } else {
        next[tierId] = Math.min(99, quantity);
      }
      syncCartInUrl(next, addonCart, cartCheckoutOpen);
      return next;
    });
  };

  const addPackageToCart = (tierId: SelfServeTierId) => {
    setPackageCart((prev) => {
      const next = { ...prev, [tierId]: Math.min(99, (prev[tierId] || 0) + 1) };
      syncCartInUrl(next, addonCart, cartCheckoutOpen);
      return next;
    });
  };

  const setAddonQuantity = (addonId: SelfServeAddonId, quantity: number) => {
    setAddonCart((prev) => {
      const next = { ...prev };
      if (quantity <= 0) {
        delete next[addonId];
      } else {
        next[addonId] = Math.min(99, quantity);
      }
      syncCartInUrl(packageCart, next, cartCheckoutOpen);
      return next;
    });
  };

  const addAddon = (addonId: SelfServeAddonId) => {
    setAddonCart((prev) => {
      const next = { ...prev, [addonId]: Math.min(99, (prev[addonId] || 0) + 1) };
      syncCartInUrl(packageCart, next, cartCheckoutOpen);
      return next;
    });
  };

  const clearCart = () => {
    setPackageCart({});
    setAddonCart({});
    setCartCheckoutOpen(false);
    syncCartInUrl({}, {}, false);
  };

  const startPackageCheckout = (tier: SelfServeTier) => {
    if (!user) {
      navigate('/auth', {
        state: {
          returnUrl: `${location.pathname}?package=${tier.id}&checkout=1${location.hash || ''}`,
        },
      });
      return;
    }

    setSelectedTier(tier);
  };

  const openCartCheckout = () => {
    const serializedPackages = serializePackageCart(packageCart);
    const serializedAddons = serializeAddonCart(addonCart);

    if (!serializedPackages && !serializedAddons) {
      toast({ title: 'Cart is empty', description: 'Add a training package or add-on before checking out.', variant: 'destructive' });
      return;
    }

    if (!user) {
      const params = new URLSearchParams();
      if (serializedPackages) params.set('cart_packages', serializedPackages);
      if (serializedAddons) params.set('cart_addons', serializedAddons);
      params.set('cart_checkout', '1');

      navigate('/auth', {
        state: {
          returnUrl: `${location.pathname}?${params.toString()}${location.hash || '#self-serve'}`,
        },
      });
      return;
    }

    setCartCheckoutOpen(true);
    syncCartInUrl(packageCart, addonCart, true);
  };

  const handleCartCheckout = async () => {
    if (!user || !hasCartItems) return;

    setCartCheckoutLoading(true);
    try {
      const returnParams = new URLSearchParams(location.search);
      returnParams.delete('cart_success');
      returnParams.delete('cart_canceled');
      returnParams.delete('session_id');

      const serializedPackages = serializePackageCart(packageCart);
      const serializedAddons = serializeAddonCart(addonCart);
      if (serializedPackages) returnParams.set('cart_packages', serializedPackages);
      if (serializedAddons) returnParams.set('cart_addons', serializedAddons);
      returnParams.set('cart_checkout', '1');

      const returnUrl = `${window.location.origin}${location.pathname}?${returnParams.toString()}${location.hash || '#self-serve'}`;
      const { data, error } = await supabase.functions.invoke('create-self-serve-cart-checkout', {
        body: {
          packageItems: packageCartItems.map((item) => ({ id: item.tier.id, quantity: item.quantity })),
          addonItems: addonCartItems.map((item) => ({ id: item.addon.id, quantity: item.quantity })),
          returnUrl,
        },
      });

      if (error || !data?.url) {
        throw new Error(data?.error || error?.message || 'Could not initialize cart checkout.');
      }

      window.location.assign(data.url);
    } catch (error: any) {
      toast({
        title: 'Could not start checkout',
        description: error.message || 'Something went wrong while creating the checkout session.',
        variant: 'destructive',
      });
      setCartCheckoutLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const packageId = params.get('package');
    const shouldOpenPackageCheckout = params.get('checkout') === '1';
    if (user && packageId && shouldOpenPackageCheckout) {
      const tier = selfServeTiers.find((entry) => entry.id === packageId);
      if (tier) {
        setSelectedTier(tier);
      }
    }

    const parsedPackageCart = parsePackageCart(params.get('cart_packages'));
    const parsedAddonCart = parseAddonCart(params.get('cart_addons'));
    setPackageCart(parsedPackageCart);
    setAddonCart(parsedAddonCart);

    if (params.get('cart_success') === 'true') {
      toast({ title: 'Purchase completed', description: 'Your self-serve cart purchase completed successfully.' });
      setCartCheckoutOpen(false);
      setPackageCart({});
      setAddonCart({});
      const cleaned = new URLSearchParams(location.search);
      cleaned.delete('cart_success');
      cleaned.delete('cart_canceled');
      cleaned.delete('session_id');
      cleaned.delete('cart_packages');
      cleaned.delete('cart_addons');
      cleaned.delete('cart_checkout');
      navigate(`${location.pathname}${cleaned.toString() ? `?${cleaned.toString()}` : ''}${location.hash || ''}`, {
        replace: true,
      });
      return;
    }

    if (params.get('cart_canceled') === 'true') {
      toast({ title: 'Checkout canceled', description: 'Your cart was kept so you can try again.' });
      if (user && (serializePackageCart(parsedPackageCart) || serializeAddonCart(parsedAddonCart))) {
        setCartCheckoutOpen(true);
      }
      const cleaned = new URLSearchParams(location.search);
      cleaned.delete('cart_canceled');
      cleaned.delete('session_id');
      navigate(`${location.pathname}${cleaned.toString() ? `?${cleaned.toString()}` : ''}${location.hash || ''}`, {
        replace: true,
      });
      return;
    }

    if (user && params.get('cart_checkout') === '1' && (serializePackageCart(parsedPackageCart) || serializeAddonCart(parsedAddonCart))) {
      setCartCheckoutOpen(true);
      return;
    }

    if (params.get('cart_checkout') !== '1') {
      setCartCheckoutOpen(false);
    }
  }, [location.search, location.pathname, location.hash, navigate, toast, user]);

  return (
    <>
      <section id="self-serve" className="px-4 py-20">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl text-center"
          >
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              Self-Serve
            </Badge>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight md:text-5xl">Launch sovereign AI training in minutes.</h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              Upload your corpus, select a model, and launch private AI training on Canadian-hosted sovereign infrastructure.
              Each package is a one-time training credit that becomes active in your dashboard after purchase.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {selfServeTiers.map((tier, idx) => {
              const Icon = TIER_ICONS[tier.id] ?? Cpu;
              const featured = tier.featured;
              const quantityInCart = packageCart[tier.id] || 0;

              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: 0.08 * idx }}
                  className="relative"
                >
                  {featured ? (
                    <div className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-primary/40 bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-foreground shadow-lg">
                      Most popular
                    </div>
                  ) : null}
                  <Card
                    className={`flex h-full flex-col border-border/70 bg-background/80 backdrop-blur transition ${
                      featured
                        ? 'border-primary/40 shadow-[0_30px_80px_-40px_rgba(220,38,38,0.6)]'
                        : 'shadow-[0_18px_60px_-40px_rgba(220,38,38,0.4)]'
                    }`}
                  >
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl font-semibold tracking-tight">{tier.name}</CardTitle>
                        <CardDescription className="mt-2 text-sm">{tier.tagline}</CardDescription>
                      </div>
                      <div className="pt-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold tracking-tight">{tier.price}</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{tier.priceUnit}</p>
                      </div>
                    </CardHeader>

                    <CardContent className="flex flex-1 flex-col gap-5">
                      <p className="text-sm leading-6 text-muted-foreground">{tier.description}</p>

                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">Includes</p>
                        <ul className="mt-3 space-y-2">
                          {tier.inclusions.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-sm leading-6">
                              <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">Ideal for</p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {tier.idealFor.map((item) => (
                            <span key={item} className="rounded-full border border-border/70 bg-muted/40 px-2.5 py-1 text-xs text-foreground/80">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Limits</p>
                        <ul className="mt-3 space-y-1.5">
                          {tier.limits.map((item) => (
                            <li key={item} className="text-xs leading-5 text-muted-foreground">
                              · {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {quantityInCart > 0 ? (
                        <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary/80">
                          {quantityInCart} credit{quantityInCart === 1 ? '' : 's'} in cart
                        </p>
                      ) : null}

                      <div className="mt-auto grid gap-2 sm:grid-cols-2">
                        <Button
                          type="button"
                          className={`gap-2 ${featured ? '' : 'bg-foreground text-background hover:bg-foreground/90'}`}
                          variant={featured ? 'default' : 'secondary'}
                          onClick={() => startPackageCheckout(tier)}
                        >
                          Buy now
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="outline" className="gap-2" onClick={() => addPackageToCart(tier.id)}>
                          Add to cart{quantityInCart > 0 ? ` (${quantityInCart})` : ''}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="mt-20"
          >
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Optional add-ons
              </Badge>
              <h3 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">Extend, deploy, and evaluate.</h3>
              <p className="mt-4 text-base leading-7 text-muted-foreground md:text-lg">
                Add training packages and optional add-ons to the same cart, then check out together in one Stripe purchase.
              </p>
            </div>

            <div className="mt-8 rounded-2xl border border-border/70 bg-background/70 p-5 backdrop-blur">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                    {cartSummaryLabel}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Due now: {formatMoney(oneTimeTotal, 'cad')} · Recurring monthly: {formatMoney(recurringMonthlyTotal, 'cad')}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button variant="outline" onClick={clearCart} disabled={!hasCartItems}>
                    Clear cart
                  </Button>
                  <Button onClick={openCartCheckout} disabled={!hasCartItems} className="gap-2">
                    Checkout cart
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-10 space-y-8">
              {categoryOrder.map((cat) => {
                const items = grouped[cat] || [];
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary/80">{ADDON_GROUP_LABEL[cat]}</p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((addon) => {
                        const quantity = addonCart[addon.id] || 0;
                        return (
                          <div
                            key={addon.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => addAddon(addon.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                addAddon(addon.id);
                              }
                            }}
                            className={`rounded-2xl border p-5 backdrop-blur transition ${
                              quantity > 0
                                ? 'border-primary/40 bg-primary/5 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.6)]'
                                : 'border-border/70 bg-background/70 hover:border-primary/30 hover:bg-background'
                            } cursor-pointer`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p className="font-semibold leading-snug">{addon.name}</p>
                              <span className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-sm font-semibold text-primary">
                                {addon.price}
                              </span>
                            </div>
                            {addon.unit ? <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">{addon.unit}</p> : null}
                            <p className="mt-3 text-sm leading-6 text-muted-foreground">{addon.description}</p>

                            <div className="mt-5 flex items-center justify-between gap-3 border-t border-border/60 pt-4">
                              {quantity > 0 ? (
                                <div className="inline-flex items-center rounded-lg border border-border/70 bg-background/80">
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setAddonQuantity(addon.id, quantity - 1);
                                    }}
                                    className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition hover:text-foreground"
                                    aria-label={`Decrease ${addon.name} quantity`}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  <span className="min-w-10 text-center text-sm font-medium">{quantity}</span>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setAddonQuantity(addon.id, quantity + 1);
                                    }}
                                    className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition hover:text-foreground"
                                    aria-label={`Increase ${addon.name} quantity`}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Click card to add</span>
                              )}

                              {quantity > 0 ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setAddonQuantity(addon.id, 0);
                                  }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  className="gap-1"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    addAddon(addon.id);
                                  }}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                  Add to cart
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      <Dialog open={!!selectedTier} onOpenChange={(open) => (!open ? closePackageCheckoutModal() : undefined)}>
        <DialogContent className="sm:max-w-xl">
          {selectedTier ? (
            <>
              <DialogHeader>
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                  <SelectedTierIcon className="h-5 w-5" />
                </div>
                <DialogTitle>{selectedTier.name}</DialogTitle>
                <DialogDescription>
                  Purchase this one-time training package now. The credit will appear in your dashboard and will be consumed the next time you launch a matching training session.
                </DialogDescription>
              </DialogHeader>

              <TrainingPackageCheckout
                tier={selectedTier}
                onSuccess={() => {
                  closePackageCheckoutModal();
                }}
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={cartCheckoutOpen}
        onOpenChange={(open) => {
          setCartCheckoutOpen(open);
          syncCartInUrl(packageCart, addonCart, open);
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Checkout your self-serve cart</DialogTitle>
            <DialogDescription>
              Buy training packages and add-ons together in one checkout. Monthly add-ons begin recurring billing; package credits and one-time add-ons are charged immediately.
            </DialogDescription>

          </DialogHeader>

          <div className="space-y-4 py-2">
            {!hasCartItems ? (
              <div className="rounded-xl border border-dashed border-border/70 p-6 text-sm text-muted-foreground">
                Your self-serve cart is empty.
              </div>
            ) : (
              <>
                {packageCartItems.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">Training packages</p>
                    {packageCartItems.map(({ tier, quantity }) => (
                      <div key={tier.id} className="flex flex-col gap-3 rounded-xl border border-border/70 bg-background/70 p-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-medium">{tier.name}</p>
                          <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">One-time training package</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center rounded-lg border border-border/70 bg-background/80">
                            <button
                              type="button"
                              onClick={() => setPackageQuantity(tier.id, quantity - 1)}
                              className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition hover:text-foreground"
                              aria-label={`Decrease ${tier.name} quantity`}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="min-w-10 text-center text-sm font-medium">{quantity}</span>
                            <button
                              type="button"
                              onClick={() => setPackageQuantity(tier.id, quantity + 1)}
                              className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition hover:text-foreground"
                              aria-label={`Increase ${tier.name} quantity`}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="min-w-24 text-right text-sm font-semibold">
                            {formatMoney(tier.amountCents * quantity, tier.currency)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {addonCartItems.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">Add-ons</p>
                    {addonCartItems.map(({ addon, quantity }) => (
                      <div key={addon.id} className="flex flex-col gap-3 rounded-xl border border-border/70 bg-background/70 p-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-medium">{addon.name}</p>
                          <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">
                            {addon.billingType === 'recurring' ? 'Monthly recurring add-on' : 'One-time add-on'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="inline-flex items-center rounded-lg border border-border/70 bg-background/80">
                            <button
                              type="button"
                              onClick={() => setAddonQuantity(addon.id, quantity - 1)}
                              className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition hover:text-foreground"
                              aria-label={`Decrease ${addon.name} quantity`}
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="min-w-10 text-center text-sm font-medium">{quantity}</span>
                            <button
                              type="button"
                              onClick={() => setAddonQuantity(addon.id, quantity + 1)}
                              className="inline-flex h-9 w-9 items-center justify-center text-muted-foreground transition hover:text-foreground"
                              aria-label={`Increase ${addon.name} quantity`}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="min-w-24 text-right text-sm font-semibold">
                            {formatMoney(addon.amountCents * quantity, addon.currency)}
                            {addon.billingType === 'recurring' ? '/month' : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            )}

            <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Due now</span>
                <span className="font-semibold">{formatMoney(oneTimeTotal, 'cad')}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Recurring monthly</span>
                <span className="font-semibold">{formatMoney(recurringMonthlyTotal, 'cad')}</span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={clearCart} disabled={!hasCartItems || cartCheckoutLoading}>
                Clear cart
              </Button>
              <Button onClick={handleCartCheckout} disabled={!hasCartItems || cartCheckoutLoading} className="gap-2">
                {cartCheckoutLoading ? 'Redirecting…' : 'Continue to Checkout'}
                <ArrowRight className="h-4 w-4" />
              </Button>

            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
